/**
 * Bedrock API Service for AI Labs
 * WebSocket-based streaming with Bedrock Claude
 * Optimized for low latency with connection prewarming
 */

// ============================================
// 🔧 DEBUG MODE - 개발자 콘솔 로그
// ============================================
const DEBUG = true;

const log = {
  info: (category, message, data = null) => {
    if (!DEBUG) return;
    const style = 'color: #3b82f6; font-weight: bold;';
    console.log(`%c[${category}] ${message}`, style, data || '');
  },
  success: (category, message, data = null) => {
    if (!DEBUG) return;
    const style = 'color: #22c55e; font-weight: bold;';
    console.log(`%c[${category}] ✅ ${message}`, style, data || '');
  },
  warn: (category, message, data = null) => {
    if (!DEBUG) return;
    const style = 'color: #f59e0b; font-weight: bold;';
    console.log(`%c[${category}] ⚠️ ${message}`, style, data || '');
  },
  error: (category, message, data = null) => {
    if (!DEBUG) return;
    const style = 'color: #ef4444; font-weight: bold;';
    console.log(`%c[${category}] ❌ ${message}`, style, data || '');
  },
  group: (title) => {
    if (!DEBUG) return;
    console.group(`🔍 ${title}`);
  },
  groupEnd: () => {
    if (!DEBUG) return;
    console.groupEnd();
  },
  table: (data) => {
    if (!DEBUG) return;
    console.table(data);
  }
};

// WebSocket endpoint
const WS_URL = import.meta.env.VITE_LABS_WS_URL || 'wss://uknjkyctd3.execute-api.us-east-1.amazonaws.com/prod';

// HTTP API fallback
const API_URL = import.meta.env.VITE_LABS_API_URL || 'https://z6zx064ldk.execute-api.us-east-1.amazonaws.com/prod/chat';

let ws = null;
let messageCallback = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 3;
let isPrewarming = false;

/**
 * Connect to WebSocket
 */
const connectWebSocket = () => {
  return new Promise((resolve, reject) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      resolve(ws);
      return;
    }

    ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttempts = 0;
      // Send ping to warm up Lambda (reduces cold start latency)
      try {
        ws.send(JSON.stringify({ action: 'ping' }));
      } catch (e) { /* ignore */ }
      resolve(ws);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      reject(error);
    };

    ws.onclose = () => {
      console.log('WebSocket closed');
      ws = null;
    };

    ws.onmessage = (event) => {
      if (messageCallback) {
        try {
          const data = JSON.parse(event.data);
          messageCallback(data);
        } catch (e) {
          console.error('Failed to parse message:', e);
        }
      }
    };

    // Timeout for connection
    setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        ws.close();
        reject(new Error('WebSocket connection timeout'));
      }
    }, 10000);
  });
};

/**
 * Process message with WebSocket streaming
 * @param {string} message - User message
 * @param {string} tool - Tool type (table, document, search, default)
 * @param {object} callbacks - Callback functions
 * @param {string} modelId - Bedrock model ID
 * @param {Array} history - Conversation history [{role: 'user'|'assistant', content: string}]
 */
export const processWithBedrock = async (message, tool = 'default', callbacks = {}, modelId = null, history = [], conversationId = null, userId = null, extraBody = null) => {
  // 🔍 1단계: 요청 시작
  log.group('processWithBedrock 실행');
  log.info('REQUEST', '요청 파라미터', {
    tool: tool,
    modelId: modelId,
    messageLength: message.length,
    historyLength: history.length
  });
  log.info('REQUEST', '메시지 미리보기', message.substring(0, 200) + (message.length > 200 ? '...' : ''));

  try {
    callbacks.onStart?.();

    // Try WebSocket first
    try {
      await connectWebSocket();
      log.success('WS', 'WebSocket 연결됨');

      return new Promise((resolve, reject) => {
        let fullContent = '';
        let table = null;
        let chunkCount = 0;

        messageCallback = (data) => {
          switch (data.type) {
            case 'pong':
              // Lambda warmup response - ignore
              break;

            case 'start':
              log.info('STREAM', '스트리밍 시작', { tool: data.tool });
              break;

            case 'chunk':
              chunkCount++;
              fullContent += data.content;
              // 매 20번째 청크마다 로그 (너무 많으면 보기 어려움)
              if (chunkCount % 20 === 0) {
                log.info('CHUNK', `청크 수신 (#${chunkCount})`, { totalLength: fullContent.length });
              }
              callbacks.onChunk?.(data.content, fullContent);
              break;

            case 'complete':
              log.group('응답 완료');
              log.success('COMPLETE', '스트리밍 완료', {
                totalChunks: chunkCount,
                contentLength: data.content?.length || fullContent.length
              });

              // 🔍 표 데이터 감지
              if (data.table) {
                log.warn('TABLE', '⚠️ 표 데이터 감지됨!', {
                  headers: data.table.headers,
                  rowCount: data.table.rows?.length
                });
                log.table(data.table.rows?.slice(0, 3)); // 처음 3줄만
              } else {
                log.info('TABLE', '표 데이터 없음');
              }

              // 🔍 시각화 데이터 감지
              if (data.visualData) {
                log.warn('VISUAL', '시각화 데이터 감지됨!', data.visualData.type);
              }

              // 🔍 응답 내용 분석
              const contentPreview = (data.content || fullContent).substring(0, 500);
              log.info('CONTENT', '응답 미리보기', contentPreview);

              // 🔍 표 관련 키워드 감지
              const tableKeywords = ['| ---', '|---|', '표:', '데이터:', 'headers', 'rows'];
              const foundKeywords = tableKeywords.filter(k => (data.content || fullContent).includes(k));
              if (foundKeywords.length > 0) {
                log.warn('KEYWORD', '⚠️ 표 관련 키워드 발견!', foundKeywords);
              }

              log.groupEnd();

              // 💾 메시지 저장 완료 로그
              if (conversationId && userId) {
                console.log('%c[SAVE] ✅ 메시지 저장 완료 (서버)', 'color: #22c55e; font-weight: bold; font-size: 12px;');
                console.log('  대화 ID:', conversationId);
                console.log('  응답 길이:', (data.content || fullContent).length, '자');
              }

              table = data.table;
              callbacks.onComplete?.({
                content: data.content,
                table: data.table,
                visualData: data.visualData
              });
              messageCallback = null;
              log.groupEnd(); // processWithBedrock 그룹 종료
              resolve({
                content: data.content,
                table: data.table,
                visualData: data.visualData
              });
              break;

            case 'audio_complete':
              // ElevenLabs/Polly TTS/Podcast 오디오 생성 완료
              log.success('AUDIO', '🎙️ 오디오 생성 완료', {
                type: data.audio_type,
                duration: data.duration_estimate,
                hasAudioUrl: !!data.audio_url,
                segmentCount: data.audio_segments?.length,
                scriptCount: data.script?.length,
                engine: data.engine
              });

              // S3 URL 사용 (더 이상 base64 변환 불필요)
              let audioUrl = data.audio_url || null;
              log.success('AUDIO', 'S3 audioUrl 수신', audioUrl);

              // 팟캐스트 세그먼트 처리 (이미 S3 URL 포함)
              let audioSegments = [];
              if (data.audio_segments && data.audio_segments.length > 0) {
                log.info('AUDIO', `세그먼트 ${data.audio_segments.length}개 수신`);
                audioSegments = data.audio_segments.map((seg) => ({
                  speaker: seg.speaker,
                  name: seg.name,
                  text: seg.text,
                  audioUrl: seg.audio_url  // S3 URL 직접 사용
                }));

                // 메인 URL이 없으면 첫 번째 세그먼트 URL 사용
                if (!audioUrl && audioSegments.length > 0 && audioSegments[0].audioUrl) {
                  audioUrl = audioSegments[0].audioUrl;
                }
                log.success('AUDIO', `세그먼트 ${audioSegments.length}개 처리 완료`);
              }

              // 오디오 결과 객체
              const audioResult = {
                type: data.audio_type, // 'tts' or 'podcast'
                audioUrl,
                audioSegments,
                script: data.script || [],
                style: data.style,
                voice: data.voice,
                duration: data.duration_estimate,
                engine: data.engine
              };

              log.info('AUDIO', '최종 오디오 데이터', {
                type: audioResult.type,
                hasAudioUrl: !!audioResult.audioUrl,
                segmentCount: audioResult.audioSegments.length,
                engine: audioResult.engine
              });

              // 오디오 콜백 호출
              callbacks.onAudioComplete?.(audioResult);
              break;

            case 'usage_warning':
              console.warn('[USAGE]', data.message);
              callbacks.onUsageWarning?.(data);
              break;

            case 'error':
              log.error('ERROR', '서버 에러', data.error);
              log.groupEnd();
              callbacks.onError?.(new Error(data.error));
              messageCallback = null;
              reject(new Error(data.error));
              break;
          }
        };

        // Build conversation history for context (last 10 messages max)
        const recentHistory = history.slice(-10).map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        // 🔍 2단계: WebSocket 메시지 전송
        const wsPayload = {
          action: 'sendMessage',
          message,
          tool,
          modelId,
          history: recentHistory,
          conversationId,
          userId,
          ...(extraBody || {}),  // 추가 파라미터 (chart_convert 등)
        };

        log.info('SEND', 'WebSocket 전송', {
          action: wsPayload.action,
          tool: wsPayload.tool,
          modelId: wsPayload.modelId,
          historyCount: wsPayload.history.length
        });

        // 💾 메시지 저장 정보 로그
        if (conversationId && userId) {
          console.log('%c[SAVE] 💾 메시지 저장 예정', 'color: #22c55e; font-weight: bold;');
          console.log('  대화 ID:', conversationId);
          console.log('  사용자 ID:', userId);
        } else {
          console.warn('%c[SAVE] ⚠️ 메시지 저장 안됨 - ID 누락', 'color: #f59e0b; font-weight: bold;', {
            conversationId: conversationId || '없음',
            userId: userId || '없음'
          });
        }

        ws.send(JSON.stringify(wsPayload));

        // Timeout
        setTimeout(() => {
          if (messageCallback) {
            messageCallback = null;
            reject(new Error('Response timeout'));
          }
        }, 600000); // 10 minutes timeout (장문 응답 대응)
      });

    } catch (wsError) {
      log.warn('WS', 'WebSocket 실패, HTTP로 대체', wsError.message);
      log.groupEnd();
      // Fall back to HTTP API
      return await processWithHTTP(message, tool, callbacks, modelId, history);
    }

  } catch (error) {
    log.error('API', 'Bedrock API 에러', error);
    log.groupEnd();
    callbacks.onError?.(error);
    throw error;
  }
};

/**
 * HTTP fallback (non-streaming)
 */
const processWithHTTP = async (message, tool, callbacks, modelId = null, history = []) => {
  log.group('HTTP 폴백 처리');
  log.info('HTTP', '요청 준비', { tool, modelId });

  const recentHistory = history.slice(-10).map(msg => ({
    role: msg.role,
    content: msg.content
  }));

  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message, tool, modelId, history: recentHistory })
  });

  if (!response.ok) {
    const errorData = await response.json();
    log.error('HTTP', '응답 에러', errorData);
    log.groupEnd();
    throw new Error(errorData.error || `API Error: ${response.status}`);
  }

  const data = await response.json();
  log.success('HTTP', '응답 수신', {
    success: data.success,
    hasTable: !!data.table,
    hasVisualData: !!data.visualData
  });

  if (!data.success) {
    log.error('HTTP', '처리 실패', data.error);
    log.groupEnd();
    throw new Error(data.error || 'Unknown error');
  }

  // 🔍 표 데이터 감지 (HTTP)
  if (data.table) {
    log.warn('TABLE', '⚠️ HTTP 응답에 표 데이터 감지!', data.table.headers);
  }

  log.groupEnd();

  // Simulate streaming for HTTP response
  const content = data.content;
  const chunkSize = 10;
  let accumulated = '';

  for (let i = 0; i < content.length; i += chunkSize) {
    const chunk = content.slice(i, i + chunkSize);
    accumulated += chunk;
    callbacks.onChunk?.(chunk, accumulated);
    await new Promise(resolve => setTimeout(resolve, 15));
  }

  callbacks.onComplete?.({
    content: data.content,
    table: data.table,
    visualData: data.visualData
  });

  return {
    content: data.content,
    table: data.table,
    visualData: data.visualData
  };
};

/**
 * Check if WebSocket is configured
 */
export const isConfigured = () => {
  const wsUrl = import.meta.env.VITE_LABS_WS_URL;
  const apiUrl = import.meta.env.VITE_LABS_API_URL;
  return (wsUrl && !wsUrl.includes('your-')) || (apiUrl && !apiUrl.includes('your-'));
};

/**
 * Disconnect WebSocket
 */
export const disconnect = () => {
  if (ws) {
    ws.close();
    ws = null;
  }
};

/**
 * Prewarm WebSocket connection (call on page load)
 * Reduces latency for first message by establishing connection early
 */
export const prewarmConnection = async () => {
  if (isPrewarming || (ws && ws.readyState === WebSocket.OPEN)) {
    return;
  }

  isPrewarming = true;
  try {
    await connectWebSocket();
    console.log('[Labs] WebSocket prewarmed - ready for low latency');
  } catch (error) {
    console.warn('[Labs] Prewarm failed, will connect on first message:', error);
  } finally {
    isPrewarming = false;
  }
};

export default { processWithBedrock, isConfigured, disconnect, prewarmConnection };
